// src/useCalculator.js
import { useMemo } from "react";
import {
  EDGING_CARPET_WIDTHS,
  EDGING_CARPET_PRINT_WIDTHS,
  CARPET_WIDTH_CONFIGS,
  LOGO_PROCESSING_OPTIONS,
} from "./data";

export const getDiscountInfo = (totalBasePrice) => {
  let discountRate = 1,
    discountLabel = "無折扣",
    isTaxExcluded = false;
  let nextTierDiff = 0;

  const p = Math.round(totalBasePrice);
  if (p <= 2000) {
    discountRate = 1;
    discountLabel = "無折扣";
    nextTierDiff = 2000 - p;
  } else if (p <= 3800) {
    discountRate = 0.91;
    discountLabel = "9.1折";
    nextTierDiff = 3800 - p;
  } else if (p <= 6000) {
    discountRate = 0.85;
    discountLabel = "8.5折";
    nextTierDiff = 6000 - p;
  } else if (p <= 12000) {
    discountRate = 0.81;
    discountLabel = "8.1折";
    nextTierDiff = 12000 - p;
  } else if (p <= 30000) {
    discountRate = 0.76;
    discountLabel = "7.6折";
    nextTierDiff = 30000 - p;
  } else if (p <= 60000) {
    discountRate = 0.71;
    discountLabel = "7.1折";
    isTaxExcluded = true;
    nextTierDiff = 60000 - p;
  } else {
    discountRate = 0.66;
    discountLabel = "66折";
    isTaxExcluded = true;
    nextTierDiff = 0;
  }
  if (nextTierDiff < 0) nextTierDiff = 0;
  return { discountRate, discountLabel, isTaxExcluded, nextTierDiff };
};

// ── 沙發墊：客人尺寸 → 實際應製作尺寸 ──
// depth=深度, width=寬度, height=高度（僅扶手區使用），單位皆為 cm
// 回傳四捨五入至整數 cm 的 { depth, width }，報價即以此尺寸計算。
export const getSofaMakeSize = (areaType, { depth, width, height }) => {
  const d = parseFloat(depth) || 0;
  const w = parseFloat(width) || 0;
  const h = parseFloat(height) || 0;

  let makeDepth, makeWidth;
  switch (areaType) {
    case "armrest": // 扶手區：深度 x (寬度 + 高度x2)
      makeDepth = d;
      makeWidth = w + h * 2;
      break;
    case "seat": // 坐墊區：(深度+20) x (寬度+10)
      makeDepth = d + 20;
      makeWidth = w + 10;
      break;
    case "chaise_double": // 貴妃區雙邊下垂：(深度+20) x (寬度+40)
      makeDepth = d + 20;
      makeWidth = w + 40;
      break;
    case "chaise_single": // 貴妃區單邊下垂：(深度+20) x (寬度+20)
      makeDepth = d + 20;
      makeWidth = w + 20;
      break;
    case "backrest": // 靠背區：(寬度/1.414) x (寬度/1.414)，不使用深度
      makeDepth = w / 1.414;
      makeWidth = w / 1.414;
      break;
    default: // free 自由設定：直接使用輸入尺寸
      makeDepth = d;
      makeWidth = w;
  }
  return { depth: Math.round(makeDepth), width: Math.round(makeWidth) };
};

// 壓邊地毯「固定加價」(RMB/件)的單一事實來源：
// 計價與款式管理顯示均使用此函式，避免兩邊邏輯不一致。
// 優先用款式設定的 extraCost；舊資料尚未填入時仍沿用原本寫死的 +150。
export const getEdgingExtraCost = (style) => {
  if (style?.extraCost != null) return parseFloat(style.extraCost) || 0;
  return style?.name === "椰棕地毯印刷" ? 150 : 0;
};

export const getApronDiscountInfo = (totalCategoryQuantity, unitPrice) => {
  let discountRate = 1;
  let discountLabel = "無折扣";
  let isTaxExcluded = false;

  const isLowPrice = unitPrice < 350;

  if (isLowPrice) {
    if (totalCategoryQuantity >= 101) {
      discountRate = 0.71;
      discountLabel = "7.1折";
      isTaxExcluded = true;
    } else if (totalCategoryQuantity >= 51) {
      discountRate = 0.76;
      discountLabel = "7.6折";
      isTaxExcluded = true;
    } else if (totalCategoryQuantity >= 6) {
      discountRate = 0.81;
      discountLabel = "8.1折";
    } else if (totalCategoryQuantity >= 4) {
      discountRate = 0.85;
      discountLabel = "8.5折";
    } else if (totalCategoryQuantity >= 2) {
      // ★★★ 修改處：改為 9 折 ★★★
      discountRate = 0.9; 
      discountLabel = "9折";
    }
  } else {
    if (totalCategoryQuantity >= 101) {
      discountRate = 0.66;
      discountLabel = "66折";
      isTaxExcluded = true;
    } else if (totalCategoryQuantity >= 51) {
      discountRate = 0.71;
      discountLabel = "7.1折";
      isTaxExcluded = true;
    } else if (totalCategoryQuantity >= 20) {
      discountRate = 0.76;
      discountLabel = "7.6折";
    } else if (totalCategoryQuantity >= 6) {
      discountRate = 0.81;
      discountLabel = "8.1折";
    } else if (totalCategoryQuantity >= 4) {
      discountRate = 0.85;
      discountLabel = "8.5折";
    } else if (totalCategoryQuantity >= 2) {
      // ★★★ 修改處：改為 9 折 ★★★
      discountRate = 0.9;
      discountLabel = "9折";
    }
  }

  return { discountRate, discountLabel, isTaxExcluded };
};

export const useCalculator = (
  items,
  productType,
  sofaStyles,
  cushionThicknesses,
  carpetStyles,
  edgingCarpetStyles,
  wallStickerStyles,
  apronStyles
) => {
  const calculationResult = useMemo(() => {
    let totalBasePrice = 0;
    let apronGrandTotal = 0;
    let debugTotalRmbCost = 0;
    let debugTotalRmbFreight = 0;
    let debugTotalRmbWear = 0;
    let debugTotalRmbPalletFee = 0;
    let debugTotalRmbExtraCost = 0;
    let debugTotalRmbLabelFee = 0;

    let totalApronQty = 0;
    let totalHatQty = 0;
    let totalPoloQty = 0;

    if (productType === "apron") {
      items.forEach((item) => {
        const q = parseInt(item.quantity) || 0;
        let cat = item.apronCategory;
        if (!cat && item.style && apronStyles) {
          const found = apronStyles.find((s) => s.name === item.style);
          if (found) cat = found.category;
        }
        if (cat === "帽子") totalHatQty += q;
        else if (cat === "POLO工作服") totalPoloQty += q;
        else totalApronQty += q;
      });
    }

    const calculatedItems = items.map((item) => {
      // 1. 初始化變數 (置頂)
      const length = parseFloat(item.length) || 0;
      const width = parseFloat(item.width) || 0;
      const height = parseFloat(item.height) || 0;
      const quantity = parseInt(item.quantity) || 1;
      let cuttingFee = 0;
      let plateFee = 0;
      let warningMsg = "";
      
      let style = null;
      if (productType === "sofa_mat")
        style = sofaStyles.find((s) => s.name === item.style);
      if (productType === "carpet")
        style = carpetStyles.find((s) => s.name === item.style);
      if (productType === "edging_carpet")
        style = edgingCarpetStyles.find((s) => s.name === item.style);
      if (productType === "wall_sticker")
        style = wallStickerStyles.find((s) => s.name === item.style);
      if (productType === "apron")
        style = apronStyles
          ? apronStyles.find((s) => s.name === item.style)
          : null;

      let isValid = quantity >= 1;
      if (productType !== "apron") {
        // 靠背區只需寬度，其餘均需寬度與深度
        const skipDepth =
          productType === "sofa_mat" && item.sofaArea === "backrest";
        if (!(width > 0)) isValid = false;
        if (!skipDepth && !(length > 0)) isValid = false;
      }
      // 沙發墊扶手區需填高度才能換算製作尺寸
      if (productType === "sofa_mat" && item.sofaArea === "armrest" && !(height > 0))
        isValid = false;

      let blockedReason = "";

      if (
        [
          "sofa_mat",
          "edging_carpet",
          "wall_sticker",
          "carpet",
          "apron",
        ].includes(productType) &&
        !style
      )
        isValid = false;
      if (productType === "cushion") {
        const thickConfig = cushionThicknesses.find(
          (t) => t.cm == item.thickness
        );
        if (!thickConfig) isValid = false;
      }

      if (!isValid)
        return { ...item, isValid: false, finalPrice: 0, blockedReason };

      // === 圍裙專用計算邏輯 ===
      if (productType === "apron") {
        const logoOpt =
          LOGO_PROCESSING_OPTIONS.find((o) => o.value === item.logoOption) ||
          LOGO_PROCESSING_OPTIONS[0];

        let currentCategory = item.apronCategory;
        if (!currentCategory && style) currentCategory = style.category;
        const isPolo = currentCategory === "POLO工作服";
        const discountBasisQty =
          currentCategory === "帽子"
            ? totalHatQty
            : isPolo
            ? totalPoloQty
            : totalApronQty;

        const { discountRate, discountLabel, isTaxExcluded } =
          getApronDiscountInfo(discountBasisQty, style.cost);
        
        // 折扣後立即四捨五入
        const discountedProductPrice = Math.round(style.cost * discountRate);

        let processingFee = 0;
        let hasProcessing = false;
        const selectedLogos = item.selectedLogos || {};
        Object.entries(selectedLogos).forEach(([key, count]) => {
          const opt = LOGO_PROCESSING_OPTIONS.find((o) => o.value === key);
          if (opt && count > 0) {
            processingFee += opt.cost * count;
            hasProcessing = true;
          }
        });

        if (item.hasModification) {
          processingFee += 90;
          hasProcessing = true;
        }
        if (item.hasStrapChange) {
          processingFee += 150;
          hasProcessing = true;
        }

        let setupCost = 0;
        if (selectedLogos["embroidery"] > 0) setupCost = 500;
        else if (selectedLogos["heat_transfer"] > 0) setupCost = 300;

        const totalPlateFee = setupCost * (parseFloat(item.plateCount) || 0);

        // 上機費 1000 元，分攤至該分類的每一件
        // POLO工作服：未滿 10 件即收（不論有無加工）；其他分類：有加工且未滿 7 件才收
        let machineFee = 0;
        const machineFeeThreshold = isPolo ? 10 : 7;
        const needMachineFee = isPolo || hasProcessing;
        if (
          needMachineFee &&
          discountBasisQty < machineFeeThreshold &&
          discountBasisQty > 0
        ) {
          machineFee = (1000 / discountBasisQty) * quantity;
        }

        const totalItemPrice = Math.round(
          (discountedProductPrice + processingFee) * quantity +
            totalPlateFee +
            machineFee
        );
        apronGrandTotal += totalItemPrice;

        const apronBaseCost = Math.round(style.cost * 0.4);
        let processCostPerItem = 0;
        const sLogos = item.selectedLogos || {};

        if (sLogos["embroidery"] > 0) {
          processCostPerItem += 45 * sLogos["embroidery"];
        }
        if (sLogos["heat_transfer"] > 0) {
          processCostPerItem += 30 * sLogos["heat_transfer"];
        }

        if (item.hasModification) processCostPerItem += 45;
        if (item.hasStrapChange) processCostPerItem += 75;

        let setupCostForCost = 0;
        if (sLogos["embroidery"] > 0) setupCostForCost = 200;
        else if (sLogos["heat_transfer"] > 0) setupCostForCost = 150;

        const totalSetupCostForCost =
          setupCostForCost * (parseFloat(item.plateCount) || 0);

        const totalCost = Math.round(
          (apronBaseCost + processCostPerItem) * quantity +
            totalSetupCostForCost
        );

        return {
          ...item,
          isValid: true,
          finalPrice: totalItemPrice,
          discountLabel,
          isTaxExcluded,
          styleName: style.name,
          details: {
            unitPrice: style.cost,
            discountRate,
            discountLabel,
            processingFeePerItem: processingFee,
            plateFee: totalPlateFee,
            machineFee: Math.round(machineFee),
            setupCost,
            totalCost,
            hasProcessing,
            apronCategory: currentCategory,
          },
        };
      }

      // === 舊產品邏輯 ===
      let chargeWidth = width;
      let chargeLength = length;

      // 沙發墊：依部位公式，將客人提供的尺寸換算為實際應製作尺寸後計價
      let sofaMakeSize = null;
      if (productType === "sofa_mat") {
        sofaMakeSize = getSofaMakeSize(item.sofaArea, {
          depth: length,
          width,
          height,
        });
        chargeLength = sofaMakeSize.depth;
        chargeWidth = sofaMakeSize.width;
      }
      if (productType === "edging_carpet" && style) {
        const isPrint = style.name.includes("印刷");
        const config = (
          isPrint ? EDGING_CARPET_PRINT_WIDTHS : EDGING_CARPET_WIDTHS
        ).find((c) => style.name.includes(c.keyword));
        if (config) {
          const closest = config.widths
            .sort((a, b) => a - b)
            .find((w) => w >= width);
          if (closest) {
            const diff = closest - width;
            if (diff > 0 && diff < 40) {
              chargeWidth = closest;
              warningMsg = `(接近幅寬${closest}cm，已自動調整計價)`;
            }
          }
        }
      }
      if (productType === "carpet" && style) {
        const config = CARPET_WIDTH_CONFIGS.find((c) =>
          c.names.includes(style.name)
        );
        if (config) {
          const closest = config.widths
            .sort((a, b) => a - b)
            .find((w) => w >= width);
          if (closest && closest > width) {
            chargeWidth = closest;
            warningMsg = `(未滿${closest}cm，以${closest}cm計價)`;
          }
        }
      }

      const areaM2 = (chargeLength / 100) * (chargeWidth / 100);
      let rmbProductCost = 0,
        rmbFreight = 0,
        rmbWear = 0,
        itemTotalBase = 0,
        unitCostRef = 0;
      let autoAddFees = parseFloat(item.processFee) || 0;
      let strapCost = 0,
        palletFee = 0,
        rmbPalletFee = 0,
        rmbExtraCost = 0,
        rmbLabelFee = 0,
        transportFee = 0;
      const styleCost = style ? style.cost : 0;

      if (productType === "dust_mite") {
        rmbProductCost = areaM2 * 65;
        rmbFreight = areaM2 * 2.57 * 8;
        rmbWear = 15;
        
        // 除塵墊總面積判斷邏輯
        let totalDustMiteArea = 0;
        if (productType === "dust_mite") {
          items.forEach((it) => {
            const l = parseFloat(it.length) || 0;
            const w = parseFloat(it.width) || 0;
            const q = parseInt(it.quantity) || 1;
            totalDustMiteArea += l * w * q;
          });
        }

        const area = length * width;
        const isSmallTotalArea = totalDustMiteArea > 0 && totalDustMiteArea < 5400;

        if (isSmallTotalArea) {
            warningMsg = "(總面積低於5400CM，另加300)";
        }

        if (item.hasCuttingFee || isSmallTotalArea) {
            cuttingFee = 300 * quantity;
        }
        
        if (item.hasPlateFee) {
            plateFee = 300;
        }

      } else if (productType === "sofa_mat") {
        rmbProductCost = areaM2 * styleCost;
        rmbFreight = areaM2 * (style?.freightRate ?? 8);
        rmbWear = 20;
        unitCostRef = styleCost;
      } else if (productType === "cushion") {
        const thickConfig = cushionThicknesses.find(
          (t) => t.cm == item.thickness
        );
        const materialMult = parseFloat(item.materialMult) || 1;
        const safeCushionType = item.cushionType || "一般坐墊";
        const is35D = item.density === "35D";
        const isTechCloth =
          safeCushionType.includes("科技布") ||
          safeCushionType === "單布套(科技布)";
        const isPillow = safeCushionType.includes("抱枕套");
        const isSingleCover = safeCushionType.includes("單布套");

        if (isPillow) {
          const pc = cushionThicknesses.find((t) => t.cm === 0) || {
            m35: 85,
            t35: 100,
            m45: 110,
            t45: 120,
          };
          unitCostRef =
            safeCushionType === "一般抱枕套"
              ? pc.m35
              : safeCushionType === "科技布抱枕套"
              ? pc.t35
              : safeCushionType === "一般抱枕套+枕心"
              ? pc.m45
              : pc.t45;
        } else {
          if (safeCushionType === "單布套(棉麻)") unitCostRef = 85;
          else if (safeCushionType === "單布套(科技布)") unitCostRef = 100;
          else {
            const tc = thickConfig || { t35: 0, t45: 0, m35: 0, m45: 0 };
            unitCostRef = isTechCloth
              ? is35D
                ? tc.t35
                : tc.t45
              : is35D
              ? tc.m35
              : tc.m45;
          }
        }
        let shapeFee = safeCushionType.includes("靠墊") ? 10 : 0;
        if (
          !isPillow &&
          (length > 100 ||
            width > 100 ||
            length / width > 2 ||
            length / width < 0.5)
        )
          shapeFee += 10;
        if (item.isIrregular) shapeFee += 10;
        if (item.isRounded) shapeFee += 10;
        if (item.isDoubleSided) shapeFee += 10;
        if (
          safeCushionType.includes("靠墊") &&
          item.cushionOption &&
          item.cushionOption !== "A"
        )
          shapeFee += 10;
        if (item.processFee === "" || item.processFee == null)
          autoAddFees = shapeFee;

        const baseCalc = (length * width * unitCostRef) / 10000;
        rmbProductCost = isPillow
          ? baseCalc
          : isSingleCover
          ? baseCalc * materialMult
          : safeCushionType === "單海綿"
          ? baseCalc * 0.7
          : baseCalc * materialMult;
        rmbProductCost += autoAddFees;
        if (rmbProductCost < 20) rmbProductCost = 20;

        let thicknessVal = parseFloat(item.thickness) || 0;
        if (isPillow) thicknessVal = safeCushionType.includes("枕心") ? 10 : 3;
        rmbFreight = (areaM2 * thicknessVal * 38.3) / 10;
        rmbWear = 20;
        if (item.hasStraps && item.strapCount > 0)
          strapCost = item.strapCount * 30 * quantity;

        // 標籤費：每件 ¥2
        rmbLabelFee = 2;
        // 坐墊的人民幣金額一律無條件進位（不留小數）
        rmbProductCost = Math.ceil(rmbProductCost);
        rmbFreight = Math.ceil(rmbFreight);
        rmbWear = Math.ceil(rmbWear);
      } else if (productType === "carpet") {
        rmbProductCost = areaM2 * styleCost + autoAddFees;
        if (length >= 150 && width >= 150) {
          palletFee = 200;
          rmbPalletFee = palletFee;
          rmbProductCost += palletFee;
          transportFee = 1500 * quantity;
        }
        rmbFreight = areaM2 * (style?.freightRate ?? 25);
        rmbWear = 20;
        unitCostRef = styleCost;
      } else if (productType === "edging_carpet") {
        rmbProductCost = areaM2 * styleCost;
        const extraCost = getEdgingExtraCost(style);
        if (extraCost > 0) {
          rmbExtraCost = extraCost;
          rmbProductCost += extraCost;
        }
        if (length >= 150 && width >= 150) {
          palletFee = 200;
          rmbPalletFee = palletFee;
          rmbProductCost += palletFee;
          transportFee = 1500 * quantity;
        }
        // 優先使用款式設定的 freightRate，無設定則預設 36
        const freightRate = style?.freightRate ?? 36;
        rmbFreight = areaM2 * freightRate;
        rmbWear = 20;
        unitCostRef = styleCost;
      } else if (productType === "wall_sticker") {
        let uP =
          styleCost +
          (item.wsThickness === "5" ? 40 : item.wsThickness === "8" ? 80 : 0) +
          (item.wsFireType === "fire" ? 40 : 0);
        rmbProductCost = areaM2 * uP;
        rmbFreight = areaM2 * (style?.freightRate ?? 48);
        rmbWear = 8 + areaM2 * 15;
        unitCostRef = uP;
      }

      const rmbTotalImport =
        rmbProductCost + rmbFreight + rmbWear + rmbLabelFee;
      const twdImport = rmbTotalImport * 5;
      const twdBase = twdImport * 3;
      itemTotalBase = Math.round(twdBase * quantity);
      totalBasePrice += itemTotalBase;
      debugTotalRmbCost +=
        (rmbProductCost - rmbPalletFee - rmbExtraCost) * quantity;
      debugTotalRmbFreight += rmbFreight * quantity;
      debugTotalRmbWear += rmbWear * quantity;
      debugTotalRmbPalletFee += rmbPalletFee * quantity;
      debugTotalRmbExtraCost += rmbExtraCost * quantity;
      debugTotalRmbLabelFee += rmbLabelFee * quantity;

      if (
        (productType === "edging_carpet" || productType === "carpet") &&
        item.hasPlateFee
      ) {
        plateFee = 300;
      }

      return {
        ...item,
        isValid: true,
        areaM2: areaM2.toFixed(2),
        chargeWidth,
        sofaMakeSize,
        twdBaseSellingPrice: itemTotalBase,
        strapCost,
        transportFee,
        palletFee,
        cuttingFee,
        plateFee,
        styleName: style?.name || null,
        calculatedProcessFee: autoAddFees,
        blockedReason,
        warningMsg,
        details: {
          rmbProductCost: (
            rmbProductCost -
            rmbPalletFee -
            rmbExtraCost
          ).toFixed(2),
          rmbExtraCost: rmbExtraCost > 0 ? rmbExtraCost.toFixed(2) : null,
          rmbLabelFee: rmbLabelFee > 0 ? rmbLabelFee.toFixed(2) : null,
          rmbPalletFee: rmbPalletFee > 0 ? rmbPalletFee.toFixed(2) : null,
          rmbFreight: rmbFreight.toFixed(2),
          rmbWear: rmbWear.toFixed(2),
          rmbTotalImport: rmbTotalImport.toFixed(2),
          twdImport: Math.round(twdImport),
          unitCost: unitCostRef,
        },
      };
    });

    if (productType === "apron") {
      return {
        items: calculatedItems,
        grandTotal: apronGrandTotal,
        discountLabel: "依分類件數折扣",
        isTaxExcluded: calculatedItems.some((i) => i.isTaxExcluded),
        nextTierDiff: 0,
        hasValidItems: calculatedItems.filter((i) => i.isValid).length > 0,
        debugData: { totalRmbCost: 0, totalRmbFreight: 0, totalRmbWear: 0 },
      };
    }

    const { discountRate, discountLabel, isTaxExcluded, nextTierDiff } =
      getDiscountInfo(totalBasePrice);
    const finalItems = calculatedItems.map((item) => {
      if (!item.isValid) return item;

      const edgingProcessFee =
        productType === "edging_carpet" ? item.calculatedProcessFee : 0;

      const finalPrice =
        Math.round(item.twdBaseSellingPrice * discountRate) +
        (item.strapCost || 0) +
        (item.transportFee || 0) +
        (item.cuttingFee || 0) +
        (item.plateFee || 0) +
        edgingProcessFee;

      return { ...item, finalPrice, discountLabel, isTaxExcluded };
    });

    return {
      items: finalItems,
      grandTotal: finalItems.reduce((sum, i) => sum + (i.finalPrice || 0), 0),
      discountLabel,
      isTaxExcluded,
      nextTierDiff,
      hasValidItems: finalItems.filter((i) => i.isValid).length > 0,
      debugData: {
        totalRmbCost: debugTotalRmbCost,
        totalRmbFreight: debugTotalRmbFreight,
        totalRmbWear: debugTotalRmbWear,
        totalRmbPalletFee: debugTotalRmbPalletFee,
        totalRmbExtraCost: debugTotalRmbExtraCost,
        totalRmbLabelFee: debugTotalRmbLabelFee,
      },
    };
  }, [
    items,
    productType,
    sofaStyles,
    cushionThicknesses,
    carpetStyles,
    edgingCarpetStyles,
    wallStickerStyles,
    apronStyles,
  ]);

  return calculationResult;
};